/**
 * Password Strength Meter Component
 *
 * Uses zxcvbn for realistic password strength estimation.
 * Shows visual feedback and suggestions to users.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import zxcvbn from 'zxcvbn';
import { Icons } from '../Icons';

interface PasswordStrengthMeterProps {
  password: string;
  /** Minimum required zxcvbn score (0–4) for `isValid` to be true. */
  minScore?: number;
  /**
   * Called whenever `score`, `isValid`, or `password` changes. Fires from
   * a `useEffect` (not during render), so the callback may safely call
   * setState in the parent.
   *
   * **Memoization contract**: the parent SHOULD wrap the callback in
   * `useCallback` (or define it stably with useState's setter). Passing
   * a fresh function on each render is allowed but causes the meter's
   * effect to re-fire on every keystroke — wasted work, not a bug.
   * All in-tree consumers (AuthModal, AIConsensusAuthModal,
   * PasswordResetConfirmPage) pass either a stable setter or a
   * useCallback wrapper.
   */
  onStrengthChange?: (score: number, isValid: boolean) => void;
}

interface StrengthLevel {
  label: string;
  color: string;
  bgColor: string;
}

export function PasswordStrengthMeter({
  password,
  minScore = 3,
  onStrengthChange,
}: PasswordStrengthMeterProps) {
  const { t } = useTranslation();

  const analysis = useMemo(() => {
    if (!password) {
      return null;
    }
    return zxcvbn(password);
  }, [password]);

  const strengthLevels: StrengthLevel[] = [
    {
      label: t('auth.passwordWeak', 'Très faible'),
      color: '#ef4444',
      bgColor: 'rgba(239, 68, 68, 0.1)',
    },
    {
      label: t('auth.passwordFair', 'Faible'),
      color: '#f97316',
      bgColor: 'rgba(249, 115, 22, 0.1)',
    },
    {
      label: t('auth.passwordGood', 'Moyen'),
      color: '#eab308',
      bgColor: 'rgba(234, 179, 8, 0.1)',
    },
    {
      label: t('auth.passwordStrong', 'Fort'),
      color: '#22c55e',
      bgColor: 'rgba(34, 197, 94, 0.1)',
    },
    {
      label: t('auth.passwordVeryStrong', 'Très fort'),
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
    },
  ];

  const score = analysis?.score ?? 0;
  const isValid = score >= minScore;
  const currentLevel = strengthLevels[score];

  // Notify parent of strength changes. Has to be a `useEffect`, not the
  // `useMemo` it used to be, because calling `onStrengthChange()` during
  // render is a React side-effect — React 18 strict mode double-fires it
  // and React 19 will warn aloud. The visible behaviour is identical
  // for consumers; this just makes the call timing predictable.
  //
  // We also notify parents when the password is empty (isValid=false)
  // so a parent that gates submission on the meter doesn't see stale
  // "valid" state after the user clears the field.
  useEffect(() => {
    if (onStrengthChange) {
      onStrengthChange(score, password ? isValid : false);
    }
  }, [score, isValid, password, onStrengthChange]);

  if (!password) {
    return null;
  }

  // Get feedback suggestions
  const suggestions = analysis?.feedback.suggestions || [];
  const warning = analysis?.feedback.warning;

  // Translate common zxcvbn warnings
  const translateWarning = (warning: string): string => {
    const warningMap: Record<string, string> = {
      'This is a top-10 common password': t('auth.pwdCommon', 'Mot de passe trop commun'),
      'This is a top-100 common password': t('auth.pwdCommon', 'Mot de passe trop commun'),
      'This is a very common password': t('auth.pwdCommon', 'Mot de passe trop commun'),
      'This is similar to a commonly used password': t('auth.pwdSimilarCommon', 'Similaire à un mot de passe courant'),
      'A word by itself is easy to guess': t('auth.pwdSingleWord', 'Un seul mot est facile à deviner'),
      'Names and surnames by themselves are easy to guess': t('auth.pwdName', 'Les noms sont faciles à deviner'),
      'Common names and surnames are easy to guess': t('auth.pwdName', 'Les noms sont faciles à deviner'),
      'Straight rows of keys are easy to guess': t('auth.pwdKeyboard', 'Séquences clavier faciles à deviner'),
      'Short keyboard patterns are easy to guess': t('auth.pwdKeyboard', 'Séquences clavier faciles à deviner'),
      'Repeats like "aaa" are easy to guess': t('auth.pwdRepeat', 'Les répétitions sont faciles à deviner'),
      'Repeats like "abcabcabc" are only slightly harder to guess than "abc"': t('auth.pwdRepeat', 'Les répétitions sont faciles à deviner'),
      'Sequences like abc or 6543 are easy to guess': t('auth.pwdSequence', 'Les séquences sont faciles à deviner'),
      'Recent years are easy to guess': t('auth.pwdYear', 'Les années récentes sont faciles à deviner'),
      'Dates are often easy to guess': t('auth.pwdDate', 'Les dates sont faciles à deviner'),
    };
    return warningMap[warning] || warning;
  };

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Strength bar */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '6px',
        }}
      >
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: level <= score ? currentLevel.color : 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      {/* Strength label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '11px',
        }}
      >
        <span
          style={{
            color: currentLevel.color,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {isValid ? (
            <Icons.CheckCircle size={12} />
          ) : (
            <Icons.AlertCircle size={12} />
          )}
          {currentLevel.label}
        </span>

        {analysis && (
          <span style={{ color: 'var(--muted)' }}>
            {analysis.crack_times_display.offline_slow_hashing_1e4_per_second}
          </span>
        )}
      </div>

      {/* Warning */}
      {warning && !isValid && (
        <div
          style={{
            marginTop: '6px',
            padding: '6px 8px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#f87171',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icons.AlertTriangle size={12} />
          {translateWarning(warning)}
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && !isValid && (
        <div style={{ marginTop: '6px' }}>
          {suggestions.slice(0, 2).map((suggestion, index) => (
            <div
              key={index}
              style={{
                fontSize: '11px',
                color: 'var(--muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: index > 0 ? '2px' : 0,
              }}
            >
              <span style={{ color: '#6366f1' }}>•</span>
              {suggestion}
            </div>
          ))}
        </div>
      )}

      {/* Minimum requirement notice */}
      {!isValid && (
        <div
          style={{
            marginTop: '6px',
            fontSize: '11px',
            color: '#f97316',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Icons.Info size={11} />
          {t('auth.passwordMinStrength', 'Le mot de passe doit être au moins "Fort"')}
        </div>
      )}
    </div>
  );
}

/**
 * Hook for password validation with zxcvbn
 */
export function usePasswordStrength(password: string, minScore = 3) {
  return useMemo(() => {
    if (!password) {
      return { score: 0, isValid: false, analysis: null };
    }
    const analysis = zxcvbn(password);
    return {
      score: analysis.score,
      isValid: analysis.score >= minScore,
      analysis,
    };
  }, [password, minScore]);
}
